import { useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, FlaskConical, Loader2, Octagon } from 'lucide-react'
import { testCompletionApi, type TestCompletionRunDetail } from '@/api/testCompletion'
import {
  useActiveTestCompletion,
  useTestCompletionActions,
  useTestCompletionCapability,
} from '@/hooks/coaching/useTestCompletion'

export function TestCompletionProgressBanner() {
  const { t } = useTranslation('coaching')
  const capability = useTestCompletionCapability()
  const active = useActiveTestCompletion(capability.data?.enabled === true)
  const learnerId = active.data?.learner.id ?? 0
  const actions = useTestCompletionActions(learnerId)
  const run = active.data
  const lastActiveRun = useRef<TestCompletionRunDetail | null>(null)
  const [completedRun, setCompletedRun] = useState<TestCompletionRunDetail | null>(null)

  useEffect(() => {
    const previousRun = lastActiveRun.current
    if (run != null) lastActiveRun.current = run
    else if (active.isSuccess) lastActiveRun.current = null

    if (!active.isSuccess || previousRun == null || previousRun.id === run?.id) return

    let cancelled = false
    void testCompletionApi.detail(previousRun.id).then(
      (finishedRun) => {
        if (!cancelled && finishedRun.status === 'COMPLETED') setCompletedRun(finishedRun)
      },
      () => undefined,
    )
    return () => {
      cancelled = true
    }
  }, [active.isSuccess, run])

  const denominator = Math.max(run?.counts.process ?? 0, 1)
  const percent = Math.min(100, Math.round(((run?.counts.success ?? 0) / denominator) * 100))
  return (
    <>
      {run ? (
        <section className="overflow-hidden rounded-2xl border border-cyan-200 bg-slate-950 text-white shadow-lg" aria-live="polite">
          <div className="flex flex-wrap items-center gap-4 px-5 py-4">
            <div className="flex size-10 items-center justify-center rounded-xl bg-cyan-400/15 text-cyan-300">
              {run.status === 'RUNNING' ? (
                <Loader2 className="size-5 animate-spin" aria-hidden="true" />
              ) : (
                <FlaskConical className="size-5" aria-hidden="true" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-cyan-300">
                {t('testCompletion.banner.eyebrow')}
              </p>
              <p className="truncate text-sm font-bold">
                {t('testCompletion.banner.summary', {
                  learner: run.learner.name,
                  endpoint: run.endpoint.title,
                  current: run.currentOrdinal ?? 0,
                  total: run.counts.total,
                })}
              </p>
            </div>
            <button
              type="button"
              onClick={() => actions.cancel.mutate(run.id)}
              disabled={run.status === 'CANCEL_REQUESTED' || actions.cancel.isPending}
              className="inline-flex items-center gap-1.5 rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-xs font-black hover:bg-white/15 disabled:opacity-50"
            >
              <Octagon className="size-4" />
              {run.status === 'CANCEL_REQUESTED'
                ? t('testCompletion.banner.cancelling')
                : t('testCompletion.banner.cancel')}
            </button>
          </div>
          <div className="h-1.5 bg-white/10">
            <div className="h-full bg-cyan-400 transition-[width] duration-500" style={{ width: `${percent}%` }} />
          </div>
        </section>
      ) : null}

      <TestCompletionCompleteDialog
        run={completedRun}
        onClose={() => setCompletedRun(null)}
      />
    </>
  )
}

function TestCompletionCompleteDialog({
  run,
  onClose,
}: {
  run: TestCompletionRunDetail | null
  onClose: () => void
}) {
  const { t } = useTranslation('coaching')
  const titleId = useId()
  const descriptionId = useId()
  const confirmRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (run == null) return
    confirmRef.current?.focus()
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose, run])

  if (run == null) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/55 p-4 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onClick={onClose}
    >
      <div
        className="w-full max-w-md overflow-hidden rounded-3xl border border-cyan-100 bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="bg-[radial-gradient(circle_at_top_right,_rgba(34,211,238,0.22),_transparent_48%)] px-7 pb-6 pt-8 sm:px-8">
          <div className="flex size-14 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-700 ring-1 ring-cyan-100">
            <CheckCircle2 className="size-8" aria-hidden="true" />
          </div>
          <p className="mt-6 text-xs font-black uppercase tracking-[0.2em] text-cyan-700">
            {t('testCompletion.completed.eyebrow')}
          </p>
          <h2 id={titleId} className="mt-2 text-2xl font-black tracking-tight text-slate-950">
            {t('testCompletion.completed.title')}
          </h2>
          <p id={descriptionId} className="mt-3 text-sm font-medium leading-6 text-slate-600">
            {t('testCompletion.completed.message', {
              learner: run.learner.name,
              endpoint: run.endpoint.title,
            })}
          </p>
          <div className="mt-6 grid grid-cols-2 gap-2">
            <div className="rounded-2xl bg-slate-950 px-4 py-3 text-white">
              <p className="text-xs font-bold text-slate-400">{t('testCompletion.completed.successLabel')}</p>
              <p className="mt-1 text-2xl font-black tabular-nums">{run.counts.success}</p>
            </div>
            <div className="rounded-2xl bg-slate-100 px-4 py-3 text-slate-900">
              <p className="text-xs font-bold text-slate-500">{t('testCompletion.completed.skipLabel')}</p>
              <p className="mt-1 text-2xl font-black tabular-nums">{run.counts.skip}</p>
            </div>
          </div>
        </div>
        <div className="border-t border-slate-100 bg-slate-50 px-7 py-5 sm:px-8">
          <button
            ref={confirmRef}
            type="button"
            onClick={onClose}
            className="min-h-12 w-full rounded-xl bg-cyan-700 px-5 text-sm font-black text-white shadow-sm transition-colors hover:bg-cyan-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-700"
          >
            {t('testCompletion.completed.confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}
